import { useCallback, useEffect, useRef, useState } from 'react';
import { scannerReady, startScanSession, stopScanSession, subscribeScanState } from '../utils/scanTrigger';

/**
 * { available, scanning, start, stop, lastState }
 * `available` is checked on mount and again whenever the window regains focus (the scanner service can
 * come and go). `scanning` follows window.onScanState; start() also marks it optimistically and clears
 * it again after the scan's timeout plus a margin, in case the wrapper never reports back.
 */
export default function useScanner({ timeoutMs = 10000 } = {}) {
  const [available, setAvailable] = useState(() => scannerReady());
  const [scanning, setScanning] = useState(false);
  const [lastState, setLastState] = useState(null);
  const guard = useRef(null);

  useEffect(() => {
    const check = () => setAvailable(scannerReady());
    check();
    window.addEventListener('focus', check);
    return () => window.removeEventListener('focus', check);
  }, []);

  useEffect(
    () =>
      subscribeScanState((state) => {
        setLastState(state);
        setScanning(state.state === 'scanning');
        if (state.state !== 'scanning') window.clearTimeout(guard.current);
      }),
    []
  );

  useEffect(() => () => window.clearTimeout(guard.current), []);

  const start = useCallback(() => {
    const ok = startScanSession({ timeoutMs });
    if (ok) {
      setScanning(true);
      window.clearTimeout(guard.current);
      guard.current = window.setTimeout(() => setScanning(false), timeoutMs + 2000);
    } else {
      setLastState({ state: 'error', message: 'The scanner could not be started' });
      setScanning(false);
    }
    return ok;
  }, [timeoutMs]);

  const stop = useCallback(() => {
    stopScanSession();
    window.clearTimeout(guard.current);
    setScanning(false);
  }, []);

  return { available, scanning, start, stop, lastState };
}
